import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Phone, AlertTriangle, CheckCircle, Users, Globe, ArrowLeft } from 'lucide-react';
import Footer from '../../components/landing/Footer';

const SafetyInformation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const safetyTips = [
    {
      title: "Before You Travel",
      tips: [
        "Verify your travel insurance is active",
        "Share your trip details with family or friends",
        "Pack a small first-aid kit and essential medications",
        "Keep emergency contacts accessible",
        "Review local travel advisories for your destination"
      ]
    },
    {
      title: "During Your Trip",
      tips: [
        "Stay aware of your surroundings",
        "Keep valuables secure and avoid displaying cash publicly",
        "Use only TripSecure-approved transport partners",
        "Contact us immediately for any safety concerns",
        "Keep your phone charged and accessible"
      ]
    },
    {
      title: "Emergency Procedures",
      tips: [
        "Stay calm and ensure your immediate safety",
        "Contact our 24/7 Safety Line: +233 (0)20 401 4370",
        "Provide your policy number or booking ID",
        "Our support team will coordinate help wherever you are",
        "Follow instructions from local authorities"
      ]
    }
  ];

  const safetyServices = [
    {
      icon: Shield,
      title: "Travel Insurance Protection",
      description: "Get covered for trip cancellations, medical emergencies, accidents, lost luggage, or delays. Our Pay-As-You-Go (PAYG) insurance ensures you're always protected — even for short local trips.",
      color: "emerald"
    },
    {
      icon: Users,
      title: "Airport Pickup & Transfer Safety",
      description: "We work only with vetted drivers and licensed transport partners. Track your driver in real time, receive driver and vehicle details before pickup, and all rides are insured under TripSecure's safety cover.",
      color: "blue"
    },
    {
      icon: Globe,
      title: "Courier & Smart Logistics Safety",
      description: "Every package is insured and trackable. Secure delivery verification, driver background checks, route monitoring, and 24/7 support for lost or delayed items.",
      color: "purple"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      {/* Back Button */}
      <div className="fixed top-24 left-4 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Back</span>
        </motion.button>
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-emerald-600/10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-blue-300/20" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-emerald-300/20" />
        
        <div className="relative z-10 px-4 mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 border border-blue-200 mb-6">
              <Shield className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Safety Information</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl mb-6">
              Your Safety Is Our Priority
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                Travel Smart. Travel Secured.
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              At TripSecure, we believe every journey should be safe, confident, and worry-free. Whether you're flying, driving, or touring, our commitment is to protect you every step of the way — before, during, and after your trip.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safety Promise */}
      <section className="py-16 px-4 mx-auto max-w-6xl sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Safety Promise</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              TripSecure is more than just insurance — we are your safety companion. We combine real-time protection, verified partners, and 24/7 support to ensure that you and your belongings are secure wherever you go.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "Verified and trained service providers",
              "Emergency response and safety support lines",
              "Secure airport pickups and transfers",
              "Instant travel and trip coverage",
              "Real-time tracking and trip updates",
              "24/7 safety helpline availability",
              "Global medical evacuation partners",
              "Licensed transport operators"
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200"
              >
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-800">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Safety Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Safety Services We Provide</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive safety solutions designed to protect you throughout your journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safetyServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl bg-${service.color}-100 flex items-center justify-center mx-auto mb-6`}>
                  <service.icon className={`w-8 h-8 text-${service.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Safety Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Safety Tips</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Follow these guidelines to ensure a safe and secure travel experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safetyTips.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">{section.title}</h3>
                <div className="space-y-4">
                  {section.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-600">{tip}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Emergency Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-white text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-bold mb-4">24/7 Safety Assistance</h2>
          <p className="text-lg mb-6 opacity-90">
            We're always one call away. In case of emergency, contact us immediately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="tel:+233204014370"
              className="flex items-center gap-3 px-8 py-4 bg-white text-red-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span>Emergency Hotline</span>
            </a>
            <div className="text-center">
              <p className="text-lg font-semibold">+233 (0)20 401 4370</p>
              <p className="text-sm opacity-90">Available 24/7</p>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default SafetyInformation;
